/**
 * CampaignsPaywall - Componente de paywall premium para módulo de campanhas
 * 
 * Exibido quando o tenant não tem acesso ao módulo de campanhas
 * Design premium estilo Stripe com hierarquia visual clara
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Megaphone, 
  Package, 
  Crown,
  Check,
  Sparkles,
  Send,
  Clock,
  BarChart3,
  FileSpreadsheet,
  Users,
  Layers,
  Shield,
  Headphones,
  RotateCcw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePaywallData } from '@/hooks/usePaywallData';

interface CampaignsPaywallProps {
  currentPlan?: string;
}

// Dados das features com ícones e descrições
const campaignFeatures = [
  {
    icon: Send,
    title: 'Disparo em massa',
    description: 'Envie mensagens para milhares de contatos simultaneamente'
  },
  {
    icon: Clock,
    title: 'Agendamento inteligente',
    description: 'Programe campanhas para o melhor horário'
  },
  {
    icon: Layers,
    title: 'Grupos de mensagens',
    description: 'Variações automáticas para evitar bloqueios'
  },
  {
    icon: Users,
    title: 'Múltiplas inboxes',
    description: 'Distribua envios entre várias contas'
  },
  {
    icon: BarChart3,
    title: 'Relatórios detalhados',
    description: 'Acompanhe entrega, leitura e falhas em tempo real'
  },
  {
    icon: FileSpreadsheet,
    title: 'Importação CSV',
    description: 'Importe listas com variáveis personalizadas'
  },
];

export function CampaignsPaywall({ currentPlan = 'free' }: CampaignsPaywallProps) {
  const navigate = useNavigate();
  const { campaignPlan, campaignAddon, isLoading, formatPrice } = usePaywallData();

  return (
    <div className="container max-w-5xl py-16">
      {/* Hero Section Premium */}
      <div className="text-center mb-16">
        {/* Ícone com gradiente animado */}
        <div className="relative inline-flex items-center justify-center w-20 h-20 mb-6">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-orange-500/20 via-amber-500/20 to-yellow-500/20 blur-xl animate-pulse" />
          <div className="relative flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500 shadow-lg shadow-orange-500/25">
            <Megaphone className="h-10 w-10 text-white" />
          </div>
        </div>
        
        <h1 className="text-4xl font-semibold tracking-tight mb-4">
          Potencialize seu Alcance
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
          Disparos em massa automatizados para escalar suas conversas e alcançar mais clientes com menos esforço.
        </p>
        
        {/* Alert sutil */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 text-sm text-muted-foreground">
          <Shield className="h-4 w-4" />
          Este recurso não está incluído no seu plano atual
        </div>
      </div>

      {/* Features Section Premium */}
      <div className="mb-16">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-semibold mb-2">Recursos que você desbloqueia</h2>
          <div className="w-24 h-1 mx-auto rounded-full bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500" />
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaignFeatures.map((feature, idx) => (
            <div 
              key={idx} 
              className="group p-5 rounded-xl border bg-card hover:shadow-lg hover:translate-y-[-2px] transition-all duration-200"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500/10 to-amber-500/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                  <feature.icon className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <h3 className="font-medium mb-1">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cards de Ação - Hierarquia Visual Clara */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        {/* Card Add-on - Neutro */}
        <Card className="relative overflow-hidden hover:shadow-lg hover:translate-y-[-2px] transition-all duration-200">
          <div className="absolute top-4 right-4">
            <Badge variant="secondary" className="font-normal">
              Recomendado
            </Badge>
          </div>
          
          <CardContent className="pt-8 pb-6 px-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                <Package className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-semibold">Contratar Add-on</h3>
                <p className="text-sm text-muted-foreground">
                  {isLoading ? (
                    <Skeleton className="h-4 w-40" />
                  ) : (
                    campaignAddon?.description || 'Adicione ao seu plano atual'
                  )}
                </p>
              </div>
            </div>
            
            {/* Preço */}
            <div className="py-6 text-center border-y border-border/50 my-4">
              <p className="text-sm text-muted-foreground mb-1">A partir de</p>
              {isLoading ? (
                <Skeleton className="h-12 w-32 mx-auto" />
              ) : (
                <>
                  <p className="text-4xl font-bold">
                    R$ {formatPrice(campaignAddon?.price_monthly)}
                    <span className="text-base font-normal text-muted-foreground">/mês</span>
                  </p>
                  {campaignAddon?.setup_fee && campaignAddon.setup_fee > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      + taxa de ativação: R$ {formatPrice(campaignAddon.setup_fee)}
                    </p>
                  )}
                </>
              )}
            </div>
            
            {/* Benefícios rápidos */}
            <div className="space-y-2 mb-6">
              <div className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Mantém seu plano atual</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Ativação imediata</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Cancele quando quiser</span>
              </div>
            </div>
            
            <Button 
              variant="outline"
              className="w-full hover:scale-[1.02] active:scale-[0.98] transition-transform"
              onClick={() => navigate('/tenant/plans')}
              disabled={isLoading}
            >
              <Package className="mr-2 h-4 w-4" />
              Ver Add-ons Disponíveis
            </Button>
          </CardContent>
        </Card>

        {/* Card Upgrade - Enterprise Dark Premium */}
        <Card className="relative overflow-hidden bg-slate-900 text-white border-0 hover:shadow-2xl hover:shadow-purple-500/10 hover:translate-y-[-2px] transition-all duration-200">
          {/* Gradient border effect */}
          <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-purple-500/20 via-pink-500/20 to-purple-500/20 opacity-50" />
          
          <div className="absolute top-4 right-4 z-10">
            <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 font-normal">
              <Sparkles className="h-3 w-3 mr-1" />
              Mais Completo
            </Badge>
          </div>
          
          <CardContent className="relative pt-8 pb-6 px-6 z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Crown className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Fazer Upgrade</h3>
                <p className="text-sm text-slate-400">
                  {isLoading ? (
                    <Skeleton className="h-4 w-40 bg-slate-700" />
                  ) : (
                    campaignPlan?.description || 'Plano completo com todos os recursos'
                  )}
                </p>
              </div>
            </div>
            
            {/* Preço */}
            <div className="py-6 text-center border-y border-slate-700/50 my-4">
              <p className="text-sm text-slate-400 mb-1">
                {campaignPlan?.name ? `Plano ${campaignPlan.name}` : 'Planos'} a partir de
              </p>
              {isLoading ? (
                <Skeleton className="h-12 w-32 mx-auto bg-slate-700" />
              ) : (
                <p className="text-4xl font-bold text-white">
                  R$ {formatPrice(campaignPlan?.price_monthly)}
                  <span className="text-base font-normal text-slate-400">/mês</span>
                </p>
              )}
            </div>
            
            {/* Benefícios premium */}
            <div className="space-y-2 mb-6">
              {campaignPlan?.features?.slice(0, 3).map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-amber-400" />
                  <span className="text-slate-300">{feature}</span>
                </div>
              )) || (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-amber-400" />
                    <span className="text-slate-300">Campanhas ilimitadas</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-amber-400" />
                    <span className="text-slate-300">Mais inboxes e agentes</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-amber-400" />
                    <span className="text-slate-300">Suporte prioritário</span>
                  </div>
                </>
              )}
            </div>
            
            <Button 
              className="w-full bg-white text-slate-900 hover:bg-slate-100 hover:scale-[1.02] active:scale-[0.98] transition-all"
              onClick={() => navigate('/tenant/plans')}
              disabled={isLoading}
            >
              <Crown className="mr-2 h-4 w-4" />
              Ver Planos Disponíveis
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Footer de Confiança */}
      <div className="flex flex-wrap items-center justify-center gap-8 py-6 border-t">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Shield className="h-4 w-4" />
          <span>Pagamento seguro via Stripe</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Headphones className="h-4 w-4" />
          <span>Suporte dedicado</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <RotateCcw className="h-4 w-4" />
          <span>7 dias de garantia</span>
        </div>
      </div>

      <p className="text-center text-sm text-muted-foreground mt-4">
        Dúvidas? Entre em contato com nosso suporte para ajudá-lo a escolher a melhor opção.
      </p>
    </div>
  );
}
